import { BackingSkillsCombo } from '@/components/admin/agents/BackingSkillsCombo';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ApiError, api } from '@/lib/api';
import type { CreateAdminAgentRequest } from '@innermate/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';

const SLUG_REGEX = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

export function AdminAgentNew() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [skillIds, setSkillIds] = useState<string[]>([]);
  const [rateLimit, setRateLimit] = useState(20);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: CreateAdminAgentRequest) => api.adminAgentCreate(body),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: ['admin', 'agents'] });
      navigate({ to: '/admin/agents/$id', params: { id: created.id } });
    },
    onError: (err: unknown) => {
      setServerError(err instanceof ApiError ? err.message : '생성에 실패했습니다.');
    },
  });

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!SLUG_REGEX.test(slug)) errs.slug = '영소문자/숫자/하이픈만, 시작/끝은 영숫자';
    if (!name.trim()) errs.name = '이름을 입력하세요';
    if (skillIds.length === 0) errs.skillIds = 'skill 을 1 개 이상 선택하세요';
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setServerError(null);
    mutation.mutate({
      slug,
      name: name.trim(),
      description: description.trim() || null,
      skillIds,
      isEnabled: true,
      starterPrompts: [],
      toolVisibility: 'status_only',
      rateLimitPerMin: rateLimit,
    });
  };

  return (
    <PageShell
      header={
        <>
          <button
            type="button"
            onClick={() => navigate({ to: '/admin/agents' })}
            className="mb-4 inline-flex items-center gap-1 text-xs text-foreground-muted hover:text-foreground"
          >
            <ArrowLeft className="size-3.5" />
            에이전트 목록
          </button>
          <PageHeader
            title="새 에이전트"
            description="외부 서비스에 노출할 채팅 에이전트를 만듭니다. backing skill 1~4 개와 외부 표시 메타데이터를 입력하세요."
          />
        </>
      }
    >
      <div className="max-w-3xl p-4">
        {serverError ? (
          <div
            role="alert"
            className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {serverError}
          </div>
        ) : null}

        <form onSubmit={submit} className="space-y-5 rounded-lg border bg-card p-5">
          <Field label="slug (URL 식별자)" htmlFor="agent-slug" error={errors.slug}>
            <Input
              id="agent-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="wholesale-sales"
              autoComplete="off"
            />
          </Field>

          <Field label="이름" htmlFor="agent-name" error={errors.name}>
            <Input
              id="agent-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="도매 AI 영업비서"
            />
          </Field>

          <Field label="설명 (선택)" htmlFor="agent-desc">
            <textarea
              id="agent-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="외부에 표시할 한 줄 설명"
              rows={3}
              className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
            />
          </Field>

          <Field
            label="backing skill (1~4 개)"
            htmlFor="agent-skills"
            error={errors.skillIds}
            hint="2 개 이상 선택하면 cross-domain merge 모드로 system prompt 와 도구가 합쳐집니다."
          >
            <BackingSkillsCombo value={skillIds} onChange={setSkillIds} max={4} />
          </Field>

          <Field label="분당 호출 제한 (externalUserRef 별)" htmlFor="agent-rate">
            <Input
              id="agent-rate"
              type="number"
              min={0}
              max={10000}
              value={rateLimit}
              onChange={(e) => setRateLimit(Number(e.target.value) || 0)}
            />
          </Field>

          <div className="flex justify-end gap-2 border-t pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate({ to: '/admin/agents' })}
              disabled={mutation.isPending}
            >
              취소
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? '생성 중...' : '에이전트 만들기'}
            </Button>
          </div>
        </form>
      </div>
    </PageShell>
  );
}
